import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, Loader2, Sparkles, Zap, Star, Send, User, Briefcase, FileText } from "lucide-react";
import axios from "axios";

// Use relative path for standalone deployment, fallback to env variable for development
const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const ApplyNow = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const observerRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: "",
    father_name: "",
    cnic: "",
    date_of_birth: "",
    phone: "",
    email: "",
    address: "",
    education: "",
    experience: "",
    position: "",
    expected_salary: "",
    availability: "",
  });

  const positions = [
    "Call Center Agent",
    "QA Inspector",
    "Supervisor",
    "IT Support",
    "Back Office Staff",
  ];

  const experienceLevels = [
    "Fresh / No Experience",
    "Less than 1 year",
    "1-2 years",
    "2-3 years",
    "3-5 years",
    "5+ years",
  ];

  const availabilityOptions = [
    "Immediate",
    "1 week",
    "2 weeks",
    "1 month",
    "More than 1 month",
  ];

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".reveal, .slide-left, .slide-right, .zoom-reveal, .flip-in, .blur-in").forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/apply`, formData);

      if (response.data.success) {
        setIsSuccess(true);
        toast.success("Application submitted successfully!");
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div data-testid="apply-success" className="pt-20 min-h-screen bg-[#0f0f10] relative overflow-hidden">
        {/* Animated particles for success */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="particle" style={{ top: '10%', left: '10%', animationDelay: '0s' }} />
          <div className="particle" style={{ top: '20%', left: '80%', animationDelay: '1s' }} />
          <div className="particle" style={{ top: '60%', left: '20%', animationDelay: '2s' }} />
          <div className="particle" style={{ top: '70%', left: '70%', animationDelay: '0.5s' }} />
          <div className="particle" style={{ top: '40%', left: '50%', animationDelay: '1.5s' }} />
        </div>
        
        <div className="max-w-2xl mx-auto px-4 py-32 relative z-10">
          <div className="success-message text-center glass-card border border-[#6DB432]/30 rounded-2xl p-12 bouncy-entrance">
            <div className="w-20 h-20 rounded-full bg-[#6DB432]/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
              <CheckCircle className="w-10 h-10 text-[#6DB432] heartbeat" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4 gradient-text-animated">
              Thank You for Applying!
            </h2>
            <p className="text-gray-400 text-lg">
              Our HR team will contact you soon.
            </p>
            <p className="text-[#6DB432] font-bold mt-6 tracking-widest uppercase neon-text">
              Powered by PRECISION
            </p>
            
            {/* Celebration animation */}
            <div className="mt-8 flex justify-center gap-4">
              <Sparkles className="w-6 h-6 text-[#6DB432] animate-bounce" />
              <Star className="w-6 h-6 text-[#8BC064] animate-bounce" style={{ animationDelay: '0.2s' }} />
              <Sparkles className="w-6 h-6 text-[#6DB432] animate-bounce" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="apply-page" className="pt-20 min-h-screen bg-[#0f0f10] relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="particle" style={{ top: '5%', left: '5%', animationDelay: '0s' }} />
        <div className="particle" style={{ top: '15%', left: '85%', animationDelay: '2s' }} />
        <div className="particle" style={{ top: '85%', left: '15%', animationDelay: '4s' }} />
      </div>
      
      {/* Hero Section */}
      <section className="py-16 bg-[#353537]/30 relative overflow-hidden">
        {/* Floating decorations */}
        <div className="absolute top-10 right-10 animate-float opacity-30">
          <Sparkles className="w-8 h-8 text-[#6DB432] animate-spin" style={{ animationDuration: '8s' }} />
        </div>
        <div className="absolute bottom-10 left-10 animate-float opacity-25" style={{ animationDelay: '2s' }}>
          <FileText className="w-6 h-6 text-[#8BC064]" />
        </div>
        
        {/* Spotlight sweep */}
        <div className="absolute inset-0 spotlight-sweep opacity-20" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-2xl stagger-children">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2 bouncy-entrance">
              <span className="w-2 h-2 rounded-full bg-[#6DB432] animate-pulse" />
              Join Our Team
              <span className="w-8 h-px bg-gradient-to-r from-[#6DB432] to-transparent" />
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold text-white leading-tight text-reveal">
              Apply for a <span className="gradient-text-animated">Position</span>
            </h1>
            <p className="mt-4 text-gray-300 text-lg text-reveal" style={{ animationDelay: '0.3s' }}>
              Fill out the form below to apply for a position at NUVEXA LLC.
            </p>
          </div>
        </div>
        
        {/* Morphing shape */}
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-gradient-to-br from-[#6DB432]/5 to-transparent -translate-y-1/2 morph-shape" />
      </section>

      {/* Application Form */}
      <section className="py-16 relative">
        {/* Neon lines */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#6DB432]/10 to-transparent" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#8BC064]/10 to-transparent" />
        
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <form
            onSubmit={handleSubmit}
            data-testid="job-application-form"
            className="space-y-8"
          >
            {/* Personal Information */}
            <div className="flip-in glass-card border border-white/5 hover:border-[#6DB432]/20 rounded-xl p-6 sm:p-8 transition-all duration-500">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#6DB432]/20 flex items-center justify-center icon-spin">
                  <User className="w-5 h-5 text-[#6DB432]" />
                </div>
                <h2 className="text-xl font-bold text-white gradient-text-animated">
                  Personal Information
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2 group">
                  <Label htmlFor="full_name" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Full Name *
                  </Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    data-testid="input-full-name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="space-y-2 group">
                  <Label htmlFor="father_name" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Father's Name *
                  </Label>
                  <Input
                    id="father_name"
                    name="father_name"
                    data-testid="input-father-name"
                    value={formData.father_name}
                    onChange={handleChange}
                    required
                    className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                    placeholder="Enter father's name"
                  />
                </div>
                <div className="space-y-2 group">
                  <Label htmlFor="cnic" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    CNIC *
                  </Label>
                  <Input
                    id="cnic"
                    name="cnic"
                    data-testid="input-cnic"
                    value={formData.cnic}
                    onChange={handleChange}
                    required
                    className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                    placeholder="XXXXX-XXXXXXX-X"
                  />
                </div>
                <div className="space-y-2 group">
                  <Label htmlFor="date_of_birth" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Date of Birth *
                  </Label>
                  <Input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    data-testid="input-dob"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    required
                    className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                  />
                </div>
                <div className="space-y-2 group">
                  <Label htmlFor="phone" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                    placeholder="+92 XXX XXXXXXX"
                  />
                </div>
                <div className="space-y-2 group">
                  <Label htmlFor="email" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2 group">
                  <Label htmlFor="address" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Address *
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    data-testid="input-address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                    placeholder="Enter your complete address"
                  />
                </div>
              </div>
              
              {/* Shimmer effect */}
              <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#6DB432]/5 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-1000" />
              </div>
            </div>

            {/* Professional Information */}
            <div className="flip-in glass-card border border-white/5 hover:border-[#6DB432]/20 rounded-xl p-6 sm:p-8 transition-all duration-500" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#6DB432]/20 flex items-center justify-center icon-spin">
                  <Briefcase className="w-5 h-5 text-[#6DB432]" />
                </div>
                <h2 className="text-xl font-bold text-white gradient-text-animated">
                  Professional Information
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2 group">
                  <Label htmlFor="education" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Education *
                  </Label>
                  <Input
                    id="education"
                    name="education"
                    data-testid="input-education"
                    value={formData.education}
                    onChange={handleChange}
                    required
                    className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                    placeholder="e.g., Bachelor's in Business"
                  />
                </div>
                <div className="space-y-2 group">
                  <Label htmlFor="experience" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Experience *
                  </Label>
                  <Select
                    value={formData.experience}
                    onValueChange={(value) =>
                      handleSelectChange("experience", value)
                    }
                  >
                    <SelectTrigger
                      data-testid="select-experience"
                      className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white hover:border-[#6DB432]/50 transition-all duration-300"
                    >
                      <SelectValue placeholder="Select experience" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#353537] border-[#7B7B7D]/30">
                      {experienceLevels.map((level) => (
                        <SelectItem
                          key={level}
                          value={level}
                          className="text-white hover:bg-[#6DB432]/20 transition-colors duration-200"
                        >
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 group">
                  <Label htmlFor="position" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Position Applying For *
                  </Label>
                  <Select
                    value={formData.position}
                    onValueChange={(value) =>
                      handleSelectChange("position", value)
                    }
                  >
                    <SelectTrigger
                      data-testid="select-position"
                      className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white hover:border-[#6DB432]/50 transition-all duration-300"
                    >
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#353537] border-[#7B7B7D]/30">
                      {positions.map((pos) => (
                        <SelectItem
                          key={pos}
                          value={pos}
                          className="text-white hover:bg-[#6DB432]/20 transition-colors duration-200"
                        >
                          {pos}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 group">
                  <Label htmlFor="expected_salary" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Expected Salary *
                  </Label>
                  <Input
                    id="expected_salary"
                    name="expected_salary"
                    data-testid="input-salary"
                    value={formData.expected_salary}
                    onChange={handleChange}
                    required
                    className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                    placeholder="e.g., PKR 50,000"
                  />
                </div>
                <div className="space-y-2 group">
                  <Label htmlFor="availability" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                    Availability *
                  </Label>
                  <Select
                    value={formData.availability}
                    onValueChange={(value) =>
                      handleSelectChange("availability", value)
                    }
                  >
                    <SelectTrigger
                      data-testid="select-availability"
                      className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white hover:border-[#6DB432]/50 transition-all duration-300"
                    >
                      <SelectValue placeholder="Select availability" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#353537] border-[#7B7B7D]/30">
                      {availabilityOptions.map((option) => (
                        <SelectItem
                          key={option}
                          value={option}
                          className="text-white hover:bg-[#6DB432]/20 transition-colors duration-200"
                        >
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="zoom-reveal">
              <Button
                type="submit"
                data-testid="submit-application-btn"
                disabled={isSubmitting}
                className="w-full bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold py-6 text-lg rounded-md btn-glow btn-shine disabled:opacity-50 group rubberBand-hover animate-pulse-glow particle-burst"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform duration-300" />
                    Submit Application
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ApplyNow;
